import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, BarChart3, Globe2, ShieldCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: BarChart3, text: "Real KPI dashboards — CPM, CTR, CPC & frequency" },
    { icon: Sparkles, text: "AI Ad Studio powered by Claude Sonnet 4.6" },
    { icon: Globe2, text: "Global market intelligence for the KARYOW marketplace" },
    { icon: ShieldCheck, text: "Secure, server-side Meta integration layer" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0D0E] grain-overlay flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left / brand */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 py-16 relative">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-lime-400/10 blur-3xl" />
        <div className="relative max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <img src="/karyow-logo.png" alt="KARYOW" className="h-11 w-11 rounded-xl object-cover" />
            <div>
              <div className="font-display font-bold text-lg text-white leading-none">KARYOW</div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-lime-400 uppercase">AI Ads Manager</div>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]">
            Run smarter Meta ads for a <span className="text-lime-400">global marketplace</span>.
          </h1>
          <p className="mt-5 text-zinc-400 leading-relaxed text-sm sm:text-base">
            Organize campaigns, generate high-converting creative with AI, and make
            evidence-based decisions — all in one tactical command center.
          </p>

          <div className="mt-10 space-y-4">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="h-9 w-9 rounded-lg bg-[#14181B] border border-[#242B30] flex items-center justify-center shrink-0">
                  <f.icon className="h-4 w-4 text-lime-400" />
                </div>
                <span className="text-sm text-zinc-300">{f.text}</span>
              </div>
            ))}
          </div>

          <button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="mt-12 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-lime-400 hover:bg-lime-300 px-8 py-3.5 font-semibold text-[#0A0D0E] transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#0A0D0E" d="M12 11v2.8h3.9c-.2 1-1.5 3-3.9 3-2.4 0-4.3-2-4.3-4.4S9.6 8 12 8c1.3 0 2.2.6 2.7 1.1l1.9-1.8C15.4 6.1 13.9 5.4 12 5.4 8.3 5.4 5.3 8.4 5.3 12s3 6.6 6.7 6.6c3.9 0 6.4-2.7 6.4-6.5 0-.4 0-.8-.1-1.1H12z"/></svg>
            Continue with Google
          </button>
          <p className="mt-4 text-xs text-zinc-600 font-mono">Secure OAuth · No password stored</p>

          <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-[#242B30] pt-6">
            <Link to="/privacy" data-testid="login-privacy-link" className="text-xs text-zinc-500 hover:text-lime-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="login-terms-link" className="text-xs text-zinc-500 hover:text-lime-400 transition-colors">Terms of Service</Link>
            <Link to="/cookies" data-testid="login-cookies-link" className="text-xs text-zinc-500 hover:text-lime-400 transition-colors">Cookie Policy</Link>
            <Link to="/data-deletion" data-testid="login-data-deletion-link" className="text-xs text-zinc-500 hover:text-lime-400 transition-colors">Data Deletion</Link>
          </div>
        </div>
      </div>

      {/* Right / visual */}
      <div className="hidden lg:block flex-1 relative overflow-hidden border-l border-[#242B30]">
        <img
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Analytics"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D0E] via-[#0A0D0E]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="rounded-2xl bg-[#14181B]/80 backdrop-blur-xl border border-[#242B30] p-6">
            <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-lime-400">Live Meta data</div>
            <div className="mt-2 font-display text-2xl font-bold text-white">Connect Meta · go live in minutes</div>
            <p className="mt-1 text-sm text-zinc-400">Real campaigns, spend and conversions pulled straight from your ad account.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
